import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
    getProjectOperations,
    markSyncRunFailed,
    markRedeployRunFailed
} from "../../services/crmService";
import { isSuperAdminUser } from "../../services/authService";

export default function ProjectOperationsModal({ projectId, onClose, onChanged }) {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [workingRunId, setWorkingRunId] = useState("");

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const result = await getProjectOperations(projectId);
            setItems(Array.isArray(result) ? result : []);
        } catch {
            toast.error("Failed to load operation activity.");
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        load();
    }, [load]);

    const latestRunStatus = useMemo(() => {
        const map = new Map();

        items.forEach((item) => {
            const runId = item.syncRunId || item.redeployRunId;
            if (!runId) return;

            if (!map.has(runId)) {
                map.set(runId, item.status);
            }
        });

        return map;
    }, [items]);

    const canMarkFailed = (item) => {
        const runId = item.syncRunId || item.redeployRunId;
        if (!runId) return false;

        const latestStatus = latestRunStatus.get(runId);

        return latestStatus === "running" || latestStatus === "pending";
    };

    const handleMarkFailed = async (item) => {
        const runId = item.syncRunId || item.redeployRunId;
        if (!runId) return;

        try {
            setWorkingRunId(runId);

            if (item.syncRunId) {
                await markSyncRunFailed(projectId, item.syncRunId);
            } else if (item.redeployRunId) {
                await markRedeployRunFailed(projectId, item.redeployRunId);
            }

            toast.success("Run marked failed.");
            await load();
            onChanged?.();
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                "Failed to mark run failed."
            );
        } finally {
            setWorkingRunId("");
        }
    };

    return (
        <div className="project-modal-backdrop">
            <div className="project-modal project-operations-modal">
                <div className="project-modal-header">
                    <div>
                        <span className="project-kicker">Diagnostics</span>
                        <h3>Project Operations</h3>
                        <p>Sync, redeploy, support and worker activity timeline.</p>
                    </div>

                    <button type="button" onClick={onClose}>×</button>
                </div>

                {loading ? (
                    <div className="operations-loading">Loading...</div>
                ) : (
                    <div className="operations-table-wrap">
                        <table className="project-table operations-table">
                            <thead>
                                <tr>
                                    <th>Time</th>
                                    <th>Operation</th>
                                    <th>Step</th>
                                    <th>Status</th>
                                    <th>Worker</th>
                                    <th>Triggered By</th>
                                    <th>Message</th>
                                    <th></th>
                                </tr>
                            </thead>

                            <tbody>
                                {items.map((x) => {
                                    const runId = x.syncRunId || x.redeployRunId;
                                    const showMarkFailed =
                                        isSuperAdminUser() && canMarkFailed(x);

                                    return (
                                        <tr key={x.id} className={`operation-status-${x.status}`}>
                                            <td>{new Date(x.createdAtUtc).toLocaleString()}</td>
                                            <td>{x.operationType}</td>
                                            <td>{x.stepKey}</td>
                                            <td>
                                                <span className={`operation-pill ${x.status}`}>
                                                    {x.status}
                                                </span>
                                            </td>
                                            <td>{x.workerName || "-"}</td>
                                            <td>{x.triggeredBy || "-"}</td>
                                            <td className="operation-message">{x.message}</td>
                                            <td>
                                                {showMarkFailed && (
                                                    <button
                                                        type="button"
                                                        className="project-action-btn secondary"
                                                        disabled={workingRunId === runId}
                                                        onClick={() => handleMarkFailed(x)}
                                                    >
                                                        Mark Failed
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}